import { showHttpError } from '@calendly/alert'
import { httpRequest } from '@calendly/http-tools'
import { createEntityAdapter } from '@reduxjs/toolkit'

import { redirectTo } from 'actions/core/navigation'
import { rootApi } from 'root-api'

import { MET_ENDPOINT_TAGS } from './constants'

export const getManagedEventType = (id) => httpRequest({
  url: `/api/organization/managed_event_types/${id}`,
  method: 'GET'
})

export const addManagedEventTypeMemberships = (managedEventTypeId, invitationIds, membershipIds, groupIds) => httpRequest({
  url: `/api/organization/managed_event_types/${managedEventTypeId}/managed_event_type_memberships`,
  method: 'POST',
  data: { membership_ids: membershipIds, invitation_ids: invitationIds, user_group_ids: groupIds }
})

export const getManagedEventTypeMemberships = (managedEventTypeId, { scope }) => httpRequest({
  url: `/api/organization/managed_event_types/${managedEventTypeId}/managed_event_type_memberships`,
  method: 'GET',
  params: { scope }
})

export const destroyManagedEventTypeMembership = (managedEventTypeId, membershipId) => httpRequest({
  url: `/api/organization/managed_event_types/${managedEventTypeId}/managed_event_type_memberships/${membershipId}`,
  method: 'DELETE'
})

export const getOrganizationMembershipsWithSlugConflicts = (managedEventTypeIds, membershipIds, userGroupIds) => httpRequest({
  url: '/api/organization/managed_event_types/check_memberships_for_conflicts',
  method: 'GET',
  params: { managed_event_type_ids: managedEventTypeIds, membership_ids: membershipIds, user_group_ids: userGroupIds }
})

export const bulkApplyManagedEvents = (managedEventIds, organizationMembershipIds) => httpRequest({
  url: '/api/organization/managed_event_types/bulk_assign',
  method: 'POST',
  data: {
    managed_event_type_ids: managedEventIds,
    membership_ids: organizationMembershipIds,
  }
})

const managedEventTypesAdapter = createEntityAdapter({
  selectId: (instance) => instance.id
})

export const {
  selectById: selectManagedEventTypeById,
  selectAll: selectAllManagedEventTypes
} = managedEventTypesAdapter.getSelectors()

const api = rootApi.enhanceEndpoints({ addTagTypes: MET_ENDPOINT_TAGS }).injectEndpoints({
  endpoints: (build) => ({
    fetchManagedEventTypesPolicy: build.query({
      query: (params = {}) => ({
        url: '/api/managed_event_types/policy',
        params,
        validateStatus: (response) => (['200', '404'].includes(`${response}`))
      })
    }),
    fetchManagedEventTypes: build.query({
      query: ({ onlyOrgOwned = false, filterTerm, sortField, sortOrder, page, pageSize = 50 } = {}) => ({
        url: '/api/organization/managed_event_types',
        params: { page, filterTerm, onlyOrgOwned, sortField, sortOrder, pageSize }
      }),
      transformResponse: (response) => {
        const { pagination, collection } = response
        return {
          pagination,
          collection: managedEventTypesAdapter.addMany(managedEventTypesAdapter.getInitialState(), collection)
        }
      },
      providesTags: ['MANAGED_EVENT_TYPE_LIST'],
    }),
    destroyManagedEventType: build.mutation({
      query: ({ id }) => ({ url: `/api/organization/managed_event_types/${id}`, method: 'DELETE' }),
      onQueryStarted: async ({ redirectUrl }, { queryFulfilled, dispatch }) => {
        try {
          await queryFulfilled
          if (redirectUrl) {
            dispatch(redirectTo(redirectUrl))
          }
        } catch (e) {
          dispatch(showHttpError(e.error))
        }
      },
      invalidatesTags: ['MANAGED_EVENT_TYPE_LIST']
    }),
    duplicateManagedEventType: build.mutation({
      query: ({ id, userGroupUuid }) => ({
        url: `/api/organization/managed_event_types/${id}/duplicate`,
        method: 'POST',
        data: { userGroupUuid }
      }),
      invalidatesTags: ['MANAGED_EVENT_TYPE_LIST']
    })
  })
})

export const {
  useFetchManagedEventTypesPolicyQuery,
  useFetchManagedEventTypesQuery,
  useDestroyManagedEventTypeMutation,
  useDuplicateManagedEventTypeMutation
} = api
